import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Tooltip, IconButton, Popover, Dialog, DialogContent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    UndoOutlined, RedoOutlined, SaveOutlined, CheckCircleOutlined, ErrorOutlined,
    ViewQuiltOutlined, CheckCircleOutline, ArticleOutlined,
    SettingsOutlined, OpenInNewOutlined, PublicOutlined,
    AutoAwesomeOutlined, NoteAddOutlined,
} from '@mui/icons-material';

import { api } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { PRIMARY, BREAKPOINTS } from '../eventPagesTab/constants';

import { FIELD_COLOR, STEP_COLOR, SUCCESS_COLOR, SELECTION_COLOR } from './constants';
import {
    uid, makeInitialStep, makeField,
    makeDefaultSettings, normaliseSteps, serialiseSteps,
} from './helpers';

import FormTabs from './FormTabs';
import FormCanvas, { HERO_BANNER, HERO_TAGLINE, HERO_TITLE, HERO_SUBTITLE } from './FormCanvas';
import {
    PanelTabs, FieldPalette,
    FieldInspector,
    FormSettingsPanel, SuccessEditor,
    HeroBannerInspector, HeroTextInspector,
    FormTemplatesPalette,
    buildPanelTabs,
} from './LeftPanel';

import { FORM_TEMPLATES } from './formTemplates';

// ─────────────────────────────────────────────────────────────────────────────
// FormWizard — shown when the active step has no fields yet
// ─────────────────────────────────────────────────────────────────────────────
function FormWizard({ onApplyTemplate, onBlank }) {
  const WIZARD_TEMPLATES = FORM_TEMPLATES.slice(0, 2);
  const ACCENT_COLORS = [FIELD_COLOR, '#8b5cf6'];

  return (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', p: 4, gap: 3,
      background: 'linear-gradient(160deg, #f8fafc 0%, #f0f4f8 100%)',
      minHeight: '100%', overflowY: 'auto',
    }}>
      {/* Heading */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '12px', background: alpha(FIELD_COLOR, 0.1), mb: 1.5 }}>
          <AutoAwesomeOutlined sx={{ fontSize: 22, color: FIELD_COLOR }} />
        </Box>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>
          Create a new form
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#9ea8b3', mt: 0.75 }}>
          Start from a template or build from scratch
        </Typography>
      </Box>

      {/* Cards row */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2.5, width: '100%', maxWidth: 820, alignItems: 'flex-start' }}>

        {/* Template cards */}
        {WIZARD_TEMPLATES.map((tpl, i) => {
          const accent = ACCENT_COLORS[i] || FIELD_COLOR;
          return (
            <Box key={tpl.id} onClick={() => onApplyTemplate(tpl)}
              sx={{
                flex: 1, display: 'flex', flexDirection: 'column',
                borderRadius: 3, border: '2px solid #e4e9f0', overflow: 'hidden',
                cursor: 'pointer', background: '#fff', transition: 'all 0.18s',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                '&:hover': { borderColor: accent, boxShadow: `0 8px 32px ${alpha(accent, 0.18)}`, transform: 'translateY(-3px)' },
              }}
            >
              {/* Colour swatch */}
              <Box sx={{ height: 140, background: tpl.bg, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, px: 2 }}>
                <Typography sx={{ fontSize: '2rem', lineHeight: 1 }}>{tpl.icon}</Typography>
                {/* Mini field sketches */}
                <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: '5px', mt: 0.5 }}>
                  {tpl.fields.slice(0, 4).map((f, j) => (
                    <Box key={j} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <Box sx={{ height: 4, borderRadius: 1, background: alpha(accent, 0.4), width: `${40 + (j % 3) * 15}%` }} />
                      <Box sx={{ height: 8, borderRadius: 1, border: `1px solid ${alpha(accent, 0.25)}`, background: alpha('#fff', 0.6) }} />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Info */}
              <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AutoAwesomeOutlined sx={{ fontSize: 13, color: accent }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e' }}>{tpl.name}</Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', ml: 'auto' }}>{tpl.fields.length} fields</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', lineHeight: 1.5 }}>{tpl.description}</Typography>
              </Box>

              {/* Apply button */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{ py: 1, borderRadius: 2, background: accent, color: '#fff', fontSize: '0.75rem', fontWeight: 700, textAlign: 'center', letterSpacing: '0.03em' }}>
                  Use this template
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Blank card */}
        <Box onClick={onBlank}
          sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            borderRadius: 3, border: '2px dashed #d1d9e0', overflow: 'hidden',
            cursor: 'pointer', background: '#fff', transition: 'all 0.18s',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            '&:hover': { borderColor: FIELD_COLOR, boxShadow: `0 8px 32px ${alpha(FIELD_COLOR, 0.1)}`, transform: 'translateY(-3px)' },
          }}
        >
          {/* Blank visual */}
          <Box sx={{ height: 140, flexShrink: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: alpha(FIELD_COLOR, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NoteAddOutlined sx={{ fontSize: 26, color: alpha(FIELD_COLOR, 0.45) }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '60%' }}>
              {[70, 90, 55, 80, 40].map((w, i) => (
                <Box key={i} sx={{ height: 5, borderRadius: 2, background: '#e4e9f0', width: `${w}%` }} />
              ))}
            </Box>
          </Box>

          {/* Info */}
          <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <NoteAddOutlined sx={{ fontSize: 13, color: '#9ea8b3' }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e' }}>Blank Form</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', lineHeight: 1.5 }}>Start with an empty step and add fields from scratch.</Typography>
          </Box>

          {/* Start button */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ py: 1, borderRadius: 2, border: '1.5px solid #e4e9f0', color: '#5a6474', fontSize: '0.75rem', fontWeight: 700, textAlign: 'center', letterSpacing: '0.03em' }}>
              Start blank
            </Box>
          </Box>
        </Box>

      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormBuilderTab
// ─────────────────────────────────────────────────────────────────────────────

export default function FormBuilderTab({ event, form: initialForm }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { canAccessForm } = useAuth();

    // ── Forms list ────────────────────────────────────────────────────────────
    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(true);
    const [activeFormId, setActiveFormId] = useState(initialForm?._id || null);
    const [addingForm, setAddingForm] = useState(false);

    // ── Per-form step state ───────────────────────────────────────────────────
    const [[steps, activeStepId], setStepsAndActive] = useState(() => {
        const first = makeInitialStep('Step 1', 0);
        return [[first], first._id];
    });
    const [formSettings, setFormSettings] = useState(() => makeDefaultSettings(initialForm));
    const [formLoaded, setFormLoaded] = useState(false);

    const setSteps = useCallback((fn) => setStepsAndActive(([p, a]) => [typeof fn === 'function' ? fn(p) : fn, a]), []);
    const setActiveStepId = useCallback((id) => setStepsAndActive(([p]) => [p, id]), []);

    const currentStep = steps.find(s => s._id === activeStepId) || steps[0];
    const fields = currentStep?.fields || [];
    const history = currentStep?.history || [[]];
    const histIdx = currentStep?.histIdx ?? 0;

    const setStepState = useCallback((stepId, fn) => {
        setStepsAndActive(([p, a]) => [p.map(s => s._id === stepId ? { ...s, ...fn(s) } : s), a]);
    }, []);

    const pushHistory = useCallback((newFields) => {
        setStepState(activeStepId, s => {
            const next = s.history.slice(0, s.histIdx + 1).concat([newFields]);
            return { fields: newFields, history: next, histIdx: next.length - 1 };
        });
    }, [activeStepId, setStepState]);

    const undo = () => { if (histIdx > 0) setStepState(activeStepId, s => ({ fields: s.history[s.histIdx - 1], histIdx: s.histIdx - 1 })); };
    const redo = () => { if (histIdx < history.length - 1) setStepState(activeStepId, s => ({ fields: s.history[s.histIdx + 1], histIdx: s.histIdx + 1 })); };

    // ── Selection + field selection ───────────────────────────────────────────
    const [selectedId, setSelectedId] = useState(null);

    const findFieldById = useCallback((id, fieldList) => {
        for (const f of fieldList) {
            if (f.id === id) return { field: f, parentColumnGroupId: null };
            if (f.type === 'column_group') {
                for (const col of (f.columns || [])) {
                    for (const cf of (col.fields || [])) {
                        if (cf.id === id) return { field: cf, parentColumnGroupId: f.id, colId: col.id };
                    }
                }
            }
        }
        return null;
    }, []);

    const selectedMatch = findFieldById(selectedId, fields);
    const selectedField = selectedMatch?.field || null;

    // ── View mode ─────────────────────────────────────────────────────────────
    const [viewMode, setViewMode] = useState('form');
    const isSuccessView = viewMode === 'success';

    // ── Breakpoint ────────────────────────────────────────────────────────────
    const [breakpoint, setBreakpoint] = useState('desktop');
    const bpWidth = BREAKPOINTS.find(b => b.key === breakpoint)?.width || '100%';

    // ── Left panel tab ────────────────────────────────────────────────────────
    const [leftTab, setLeftTab] = useState('fields');
    const [settingsAnchor, setSettingsAnchor] = useState(null);
    const [visitModalOpen, setVisitModalOpen] = useState(false);
    const [formSlugUnavailable, setFormSlugUnavailable] = useState(false);

    // ── Load forms list ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!event) { setFormsLoading(false); return; }
        const eventId = typeof event === 'string' ? event : event._id;
        api.get(`/forms?eventId=${eventId}`)
            .then(({ data: res }) => {
                if (res.success && Array.isArray(res.data)) {
                    const accessible = res.data.filter(f => canAccessForm(f._id));
                    setForms(accessible);
                    const urlId = searchParams.get('id');
                    const target = accessible.find(f => f._id === urlId)?._id
                        || (initialForm && canAccessForm(initialForm._id) ? initialForm._id : null)
                        || (accessible[0]?._id ?? null);
                    setActiveFormId(target);
                }
            })
            .catch(() => { })
            .finally(() => setFormsLoading(false));
    }, [event, initialForm?._id, canAccessForm]);

    // ── Load active form ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!activeFormId) { setFormLoaded(true); return; }
        setFormLoaded(false);
        setSelectedId(null);
        setViewMode('form');

        api.get(`/forms/${activeFormId}`)
            .then(({ data: res }) => {
                if (res.success && res.data) {
                    const d = res.data;
                    let loadedSteps;
                    if (Array.isArray(d.steps) && d.steps.length) {
                        loadedSteps = normaliseSteps(d.steps);
                    } else {
                        const first = makeInitialStep('Step 1', 0);
                        loadedSteps = [first];
                    }

                    // If a template is pending, apply it now — before any state is set —
                    // so there is only one setState call and no race condition.
                    const template = window.__pendingTemplate__;
                    if (template) {
                        window.__pendingTemplate__ = null;
                        // Resolve login-method-aware fields if the template supports it.
                        // At this point `event` is in scope via the outer component closure.
                        const _loginMethod        = event?.login?.method || 'email';
                        const _employeeFieldLabel = forms.find(f => f._id === activeFormId)?.employeeFieldLabel || 'Employee Number';
                        const resolvedFields = typeof template.getFields === 'function'
                            ? template.getFields(_loginMethod, _employeeFieldLabel)
                            : template.fields;
                        const fresh = resolvedFields.map((f, i) => ({
                            ...f,
                            // System fields keep their stable __sys_*__ id so the submission
                            // payload uses the correct key (e.g. __sys_empnum__) not a random one.
                            id: f._systemField ? f.id : `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`,
                        }));
                        loadedSteps = loadedSteps.map((s, i) =>
                            i === 0
                                ? { ...s, fields: fresh, history: [[], fresh], histIdx: 1 }
                                : s
                        );
                        if (template.isRegistration !== undefined) {
                            setForms(prev => prev.map(f =>
                                f._id === activeFormId
                                    ? { ...f, isRegistration: template.isRegistration }
                                    : f
                            ));
                            api.put(`/forms/${activeFormId}`, { isRegistration: template.isRegistration }).catch(() => {});
                        }
                    }

                    setStepsAndActive([loadedSteps, loadedSteps[0]._id]);

                    // Single settings merge — template values win over the blank form defaults
                    const baseSettings = makeDefaultSettings(d);
                    setFormSettings(template ? {
                        ...baseSettings,
                        headerTitle:    template.headerTitle    || baseSettings.headerTitle,
                        headerSubtitle: template.headerSubtitle || baseSettings.headerSubtitle,
                        description:    template.description2   || baseSettings.description,
                        heroTextColor:  template.heroTextColor  || baseSettings.heroTextColor,
                        branding: {
                            ...(baseSettings.branding || {}),
                            ...(template.layout ? { layout: template.layout } : {}),
                        },
                    } : baseSettings);
                }
            })
            .catch(() => {})
            .finally(() => setFormLoaded(true));
    }, [activeFormId]);

    // ── Auto-save ─────────────────────────────────────────────────────────────
    const [saveStatus, setSaveStatus] = useState('idle');
    const saveTimerRef    = useRef(null);
    // Tracks which formId has already had its initial system-field sync run.
    // Prevents loginMethodMismatch from flashing true on first load before
    // the sync effect has had a chance to execute.
    const syncedFormIdRef = useRef(null);

    const saveForm = useCallback(async (stepsToSave) => {
        if (!activeFormId) return;
        setSaveStatus('saving');
        try {
            await api.put(`/forms/${activeFormId}`, {
                headerTitle:    formSettings.headerTitle,
                headerSubtitle: formSettings.headerSubtitle,
                headerTagline:  formSettings.description,
                description:    formSettings.description,
                branding:       formSettings.branding,
                successPage:    formSettings.successPage,
                steps:          serialiseSteps(stepsToSave),
            });
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2500);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        }
    }, [activeFormId, formSettings]);

    useEffect(() => {
        if (!formLoaded || !activeFormId) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => saveForm(steps), 1500);
        return () => clearTimeout(saveTimerRef.current);
    }, [steps, formSettings, formLoaded, activeFormId]);

    // ── Step management ───────────────────────────────────────────────────────
    const handleAddStep = useCallback(() => {
        setStepsAndActive(([p]) => {
            const s = makeInitialStep(`Step ${p.length + 1}`, p.length);
            return [[...p, s], s._id];
        });
    }, []);
    const handleSwitchStep  = useCallback((id) => { setActiveStepId(id); setSelectedId(null); }, [setActiveStepId]);
    const handleRenameStep  = useCallback((id, title) => { setSteps(p => p.map(s => s._id === id ? { ...s, title } : s)); }, [setSteps]);
    const handleDeleteStep  = useCallback((id) => {
        setStepsAndActive(([p, a]) => {
            if (p.length <= 1) return [p, a];
            const idx = p.findIndex(s => s._id === id);
            const n   = p.filter(s => s._id !== id);
            return [n, a === id ? n[Math.max(0, idx - 1)]._id : a];
        });
    }, []);
    const handleReorderSteps = useCallback((dragId, dropId) => {
        setStepsAndActive(([p, a]) => {
            const from = p.findIndex(s => s._id === dragId); if (from === -1) return [p, a];
            const next = [...p]; const [mv] = next.splice(from, 1);
            const to = next.findIndex(s => s._id === dropId);
            if (to === -1) next.push(mv); else next.splice(to, 0, mv);
            return [next, a];
        });
    }, []);

    // ── Field management ──────────────────────────────────────────────────────
    const [dragOverId,  setDragOverId]  = useState(null);
    const [dragFieldId, setDragFieldId] = useState(null);

    const handleDropNew = useCallback((type, beforeId) => {
        const nf = makeField(type); if (!nf) return;
        const next = [...fields];
        if (beforeId === null) { next.push(nf); } else { const i = next.findIndex(f => f.id === beforeId); next.splice(i, 0, nf); }
        pushHistory(next); setSelectedId(nf.id);
    }, [fields, pushHistory]);

    const handleFieldChange = useCallback((u) => {
        if (u.__batchOps__) {
            let next = [...fields];
            for (const op of u.__batchOps__) {
                if (op.op === 'delete') next = next.filter(f => f.id !== op.id);
                if (op.op === 'update') next = next.map(f => f.id === op.field.id ? op.field : f);
            }
            pushHistory(next);
            return;
        }
        const match = findFieldById(u.id, fields);
        if (match?.parentColumnGroupId) {
            pushHistory(fields.map(f => {
                if (f.id !== match.parentColumnGroupId) return f;
                return {
                    ...f,
                    columns: (f.columns || []).map(col => ({
                        ...col,
                        fields: (col.fields || []).map(cf => cf.id === u.id ? u : cf),
                    })),
                };
            }));
        } else {
            pushHistory(fields.map(f => f.id === u.id ? u : f));
        }
    }, [fields, pushHistory, findFieldById]);

    const handleDeleteField = useCallback((id) => {
        const f = fields.find(f => f.id === id);
        if (f?._systemField) return;
        pushHistory(fields.filter(f => f.id !== id)); setSelectedId(null);
    }, [fields, pushHistory]);

    // ── Derived login/employee values needed by handleApplyTemplate ───────────
    // Declared here (above the useCallback) so the closure can reference them
    // without hitting the temporal dead zone.
    const loginMethod        = event?.login?.method || 'email';
    const employeeFieldLabel = forms.find(f => f._id === activeFormId)?.employeeFieldLabel || 'Employee Number';

    const handleApplyTemplate = useCallback((template) => {
        // If the template is login-method-aware (registration), resolve the
        // correct system fields for the current event's login configuration.
        const resolvedFields = typeof template.getFields === 'function'
            ? template.getFields(loginMethod, employeeFieldLabel)
            : template.fields;
        const fresh = resolvedFields.map((f, i) => ({
            ...f,
            // System fields keep their stable __sys_*__ id so the submission
            // payload uses the correct key (e.g. __sys_empnum__) not a random one.
            id: f._systemField ? f.id : `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`,
        }));
        pushHistory(fresh);
        setFormSettings(prev => ({
            ...prev,
            headerTitle:    template.headerTitle    || prev.headerTitle,
            headerSubtitle: template.headerSubtitle || prev.headerSubtitle,
            description:    template.description2   || prev.description,
            heroTextColor:  template.heroTextColor  || prev.heroTextColor,
            branding: {
                ...(prev.branding || {}),
                ...(template.layout ? { layout: template.layout } : {}),
            },
        }));
        if (template.isRegistration !== undefined && activeFormId) {
            setForms(prev => prev.map(f => f._id === activeFormId ? { ...f, isRegistration: template.isRegistration } : f));
            api.put(`/forms/${activeFormId}`, { isRegistration: template.isRegistration }).catch(() => {});
        }
        setSelectedId(null);
    }, [pushHistory, activeFormId, loginMethod, employeeFieldLabel]);

    const handleReorder = useCallback((dragId, dropId) => {
        const from = fields.findIndex(f => f.id === dragId); if (from === -1) return;
        const next = [...fields]; const [mv] = next.splice(from, 1);
        if (dropId === null) { next.push(mv); } else { const to = next.findIndex(f => f.id === dropId); if (to === -1) next.push(mv); else next.splice(to, 0, mv); }
        pushHistory(next);
    }, [fields, pushHistory]);

    const handleCanvasDrop = (e, fieldId) => {
        e.preventDefault();
        const ft          = e.dataTransfer.getData('fieldType');
        const innerColFid = e.dataTransfer.getData('innerColFieldId');

        if (ft) {
            handleDropNew(ft, fieldId);
        } else if (innerColFid && window.__draggedColField__) {
            const { field: colField, colId, columnGroupId } = window.__draggedColField__;
            window.__draggedColField__ = null;
            const next = fields.map(f => {
                if (f.id !== columnGroupId) return f;
                return { ...f, columns: (f.columns || []).map(c => c.id === colId ? { ...c, fields: [] } : c) };
            });
            const insertIdx = fieldId ? next.findIndex(f => f.id === fieldId) : -1;
            const withField = [...next];
            if (insertIdx === -1) { withField.push(colField); } else { withField.splice(insertIdx, 0, colField); }
            pushHistory(withField);
            setSelectedId(colField.id);
        } else if (dragFieldId && dragFieldId !== fieldId) {
            handleReorder(dragFieldId, fieldId);
        }

        setDragOverId(null); setDragFieldId(null);
    };

    const dragHandlers = {
        onDragStart: (id) => setDragFieldId(id),
        onDragOver:  (id) => setDragOverId(id),
        onDrop:      (e, fieldId) => handleCanvasDrop(e, fieldId),
        onDropNew:   handleDropNew,
        dragOverId,
    };

    // ── Form CRUD ─────────────────────────────────────────────────────────────
    const handleSwitchForm = useCallback((formId) => {
        if (formId === activeFormId) return;
        if (!canAccessForm(formId)) return;
        setActiveFormId(formId);
        setSaveStatus('idle');
        setSearchParams({ id: formId }, { replace: true });
    }, [activeFormId, setSearchParams]);

    const handleFormCreated = useCallback((newForm) => {
        setForms(prev => [newForm, ...prev]);
        setActiveFormId(newForm._id);
        setSearchParams({ id: newForm._id }, { replace: true });
    }, [setSearchParams]);

    const handleAddForm = useCallback(async () => {
        if (addingForm) return;
        setAddingForm(true);
        try {
            const eventId = typeof event === 'string' ? event : event?._id;
            const { data: res } = await api.post('/forms', {
                name:     'New Form',
                slug:     `new-form-${Date.now()}`,
                formType: 'registration',
                status:   'draft',
                ...(eventId ? { event: eventId } : {}),
            });
            if (res.success && res.data) handleFormCreated(res.data);
        } catch {
            // best-effort
        } finally {
            setAddingForm(false);
        }
    }, [addingForm, event, handleFormCreated]);

    const handleRenameForm = useCallback(async (formId, newName) => {
        setForms(prev => prev.map(f => f._id === formId ? { ...f, name: newName } : f));
        try { await api.put(`/forms/${formId}`, { name: newName }); } catch { /* best-effort */ }
    }, []);

    const handleDuplicateForm = useCallback(async (formId) => {
        try {
            const { data: res } = await api.post(`/forms/${formId}/duplicate`);
            if (res.success && res.data) { setForms(prev => [...prev, res.data]); setActiveFormId(res.data._id); }
        } catch { alert('Duplicate failed — check your API endpoint.'); }
    }, []);

    const handleDeleteForm = useCallback(async (formId) => {
        if (!window.confirm('Delete this form? This cannot be undone.')) return;
        const remaining = forms.filter(f => f._id !== formId);
        setForms(remaining);
        if (activeFormId === formId) {
            const nextId = remaining[0]?._id || null;
            setActiveFormId(nextId);
            if (nextId) setSearchParams({ id: nextId }, { replace: true });
            else        setSearchParams({}, { replace: true });
        }
        try { await api.delete(`/forms/${formId}`); } catch { /* best-effort */ }
    }, [forms, activeFormId]);

    // ── Derived values ────────────────────────────────────────────────────────
    const accentColor    = isSuccessView ? SUCCESS_COLOR : FIELD_COLOR;
    const activeBranding = formSettings.branding || {};
    const activeLayout   = formSettings.branding?.layout || 'image-top';

    const PANEL_TABS     = buildPanelTabs();
    // Hide wizard the moment the user clicks — addingForm goes true before activeFormId is set
    const noFormSelected = !activeFormId && !formsLoading && !addingForm;
    const totalFields    = steps.reduce((acc, s) => acc + (s.stepType === 'selection' ? (s.selectionGroups?.length ?? 0) : (s.fields?.length ?? 0)), 0);

    const activeForm     = forms.find(f => f._id === activeFormId) || null;
    const isRegistration = activeForm?.isRegistration || false;

    // ── System field injection ────────────────────────────────────────────────

    const expectedSystemKeys = useCallback(() => {
        switch (loginMethod) {
            case 'email':          return new Set(['email', 'password', 'firstName', 'lastName', 'phone']);
            case 'employeeNumber': return new Set(['employeeNumber', 'firstName', 'lastName', 'phone']);
            case 'magicLink':      return new Set(['magicLink', 'firstName', 'lastName', 'phone']);
            default:               return new Set();
        }
    }, [loginMethod]);

    const staleSystemFields = fields.filter(f => f._systemField && !expectedSystemKeys().has(f._systemKey));
    // Only show the mismatch warning after the initial sync effect has already
    // run for this form. Without this guard the banner flashes on every reload
    // because staleSystemFields is computed from raw API data before the effect
    // has had a chance to fix/normalise the fields.
    const loginMethodMismatch = isRegistration
        && staleSystemFields.length > 0
        && syncedFormIdRef.current === activeFormId;

    const buildSystemFields = useCallback(() => {
        const nameAndPhone = [
            { id: '__sys_firstName__', type: 'firstName', _type: 'firstName', label: 'First Name',    placeholder: '', required: false, helperText: '', _systemField: true, _systemKey: 'firstName' },
            { id: '__sys_lastName__',  type: 'lastName',  _type: 'lastName',  label: 'Last Name',     placeholder: '', required: false, helperText: '', _systemField: true, _systemKey: 'lastName'  },
            { id: '__sys_phone__',     type: 'phone',     _type: 'phone',     label: 'Phone Number',  placeholder: '+1 (555) 000-0000', required: false, helperText: '', _systemField: true, _systemKey: 'phone' },
        ];
        switch (loginMethod) {
            case 'email':
                return [
                    { id: '__sys_email__',    type: 'email',    _type: 'email',    label: 'Email Address', placeholder: 'you@example.com', required: true, helperText: '', _systemField: true, _systemKey: 'email' },
                    { id: '__sys_password__', type: 'password', _type: 'password', label: 'Password',      placeholder: 'Enter your password',     required: true, helperText: '', _systemField: true, _systemKey: 'password' },
                    ...nameAndPhone,
                ];
            case 'employeeNumber':
                return [
                    { id: '__sys_empnum__', type: 'text', _type: 'text', label: employeeFieldLabel, placeholder: '', required: true, helperText: '', _systemField: true, _systemKey: 'employeeNumber' },
                    ...nameAndPhone,
                ];
            case 'magicLink':
                return [
                    { id: '__sys_email__', type: 'email', _type: 'email', label: 'Email Address', placeholder: 'you@example.com', required: true, helperText: '', _systemField: true, _systemKey: 'magicLink' },
                    ...nameAndPhone,
                ];
            default:
                return [];
        }
    }, [loginMethod, employeeFieldLabel]);

    // ── Single unified system-field sync effect ─────────────────────────────
    // Runs whenever the active form, registration flag, login method, or
    // employee label changes. In one pass it:
    //   1. Unlocks stale system fields (wrong _systemKey for current login method)
    //   2. Fixes the id of any system field whose _systemKey is correct but whose
    //      id doesn't match the canonical __sys_*__ value (submission key fix)
    //   3. Injects any expected system field that is missing entirely
    // Only calls pushHistory when something actually changed.
    // After the first run for a given formId, marks syncedFormIdRef so that
    // loginMethodMismatch does not flash true before this effect executes.
    useEffect(() => {
        if (!isRegistration || !activeFormId) {
            // Reset the synced marker whenever we leave a registration form
            if (!activeFormId || !isRegistration) syncedFormIdRef.current = null;
            return;
        }
        const expected  = expectedSystemKeys();
        const sysFields = buildSystemFields();
        let changed = false;
        let next = [...fields];

        // Step 1 — unlock stale fields (system key no longer valid for this login method)
        next = next.map(f => {
            if (f._systemField && !expected.has(f._systemKey)) {
                changed = true;
                return { ...f, _systemField: false, _systemKey: undefined };
            }
            return f;
        });

        // Step 2 & 3 — for each expected system field, either fix its id or inject it
        sysFields.forEach(sf => {
            const idx = next.findIndex(f => f._systemKey === sf._systemKey);
            if (idx !== -1) {
                // Field exists — fix id if wrong, fix label if employee number changed
                const f = next[idx];
                const needsIdFix    = f.id !== sf.id;
                const needsLabelFix = sf._systemKey === 'employeeNumber' && f.label !== sf.label;
                if (needsIdFix || needsLabelFix) {
                    next = [...next];
                    next[idx] = { ...f, id: sf.id, ...(needsLabelFix ? { label: sf.label } : {}) };
                    changed = true;
                }
            } else {
                // Field missing entirely — prepend it
                next = [sf, ...next];
                changed = true;
            }
        });

        if (changed) pushHistory(next);

        // Mark this form as synced so loginMethodMismatch stays false until
        // the user genuinely changes the login method after the initial load.
        syncedFormIdRef.current = activeFormId;
    // eslint-disable-next-line
    }, [activeFormId, isRegistration, loginMethod, employeeFieldLabel]);

    // handleResyncSystemFields — called by the manual "Fix fields" button.
    // Same logic as the effect above, exposed as a callback.
    const handleResyncSystemFields = useCallback(() => {
        const expected  = expectedSystemKeys();
        const sysFields = buildSystemFields();
        let next = [...fields];

        // Step 1 — unlock stale fields
        next = next.map(f =>
            f._systemField && !expected.has(f._systemKey)
                ? { ...f, _systemField: false, _systemKey: undefined }
                : f
        );

        // Steps 2 & 3 — fix id / inject missing
        sysFields.forEach(sf => {
            const idx = next.findIndex(f => f._systemKey === sf._systemKey);
            if (idx !== -1) {
                const f = next[idx];
                const needsIdFix    = f.id !== sf.id;
                const needsLabelFix = sf._systemKey === 'employeeNumber' && f.label !== sf.label;
                if (needsIdFix || needsLabelFix) {
                    next = [...next];
                    next[idx] = { ...f, id: sf.id, ...(needsLabelFix ? { label: sf.label } : {}) };
                }
            } else {
                next = [sf, ...next];
            }
        });

        pushHistory(next);
    }, [fields, buildSystemFields, expectedSystemKeys, pushHistory]);

    const visitUrl = activeForm
        ? `${process.env.REACT_APP_FRONTEND_URL || ''}/${event?.slug}/${activeForm.slug}`
        : null;

    const handleVisitForm = useCallback(async () => {
        if (!visitUrl) return;
        if (activeForm?.status !== 'published') { setVisitModalOpen(true); return; }
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        await saveForm(steps);
        window.open(visitUrl, '_blank', 'noopener,noreferrer');
    }, [activeForm, visitUrl, steps, saveForm]);

    const handlePublishAndVisitForm = useCallback(async () => {
        if (!activeFormId || !visitUrl) return;
        setVisitModalOpen(false);
        try {
            await api.put(`/forms/${activeFormId}`, { status: 'published' });
            setForms(prev => prev.map(f => f._id === activeFormId ? { ...f, status: 'published' } : f));
        } catch { /* best-effort */ }
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        await saveForm(steps);
        window.open(visitUrl, '_blank', 'noopener,noreferrer');
    }, [activeFormId, visitUrl, steps, saveForm]);

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', background: '#f0f4f8' }}
            onClick={() => { setSelectedId(null); setDragOverId(null); }}
        >
            {/* ── Toolbar ─────────────────────────────────────────────────────── */}
            <Box onClick={e => e.stopPropagation()} sx={{ background: '#fff', borderBottom: '1px solid #e8edf2', px: 3, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>

                {/* Left: undo / redo / save */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Tooltip title="Undo"><span><IconButton size="small" onClick={undo} disabled={histIdx === 0 || !activeFormId}><UndoOutlined sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
                    <Tooltip title="Redo"><span><IconButton size="small" onClick={redo} disabled={histIdx === history.length - 1 || !activeFormId}><RedoOutlined sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
                    {activeFormId && (
                        <Tooltip title={formSlugUnavailable ? 'Fix the unavailable slug before saving' : saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Save failed' : 'Save form'}>
                            <Box
                                onClick={() => { if (formSlugUnavailable || saveStatus === 'saving') return; if (saveTimerRef.current) clearTimeout(saveTimerRef.current); saveForm(steps); }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, px: 1.25, height: 30, borderRadius: 1.5, fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
                                    ...(formSlugUnavailable
                                        ? { background: alpha('#ef4444', 0.08), color: '#dc2626', border: '1px solid #fca5a5', cursor: 'not-allowed', opacity: 0.7 }
                                        : saveStatus === 'saved'
                                            ? { background: alpha('#22c55e', 0.1), color: '#16a34a', border: '1px solid #86efac', cursor: 'pointer' }
                                            : saveStatus === 'error'
                                                ? { background: alpha('#ef4444', 0.1), color: '#dc2626', border: '1px solid #fca5a5', cursor: 'pointer' }
                                                : saveStatus === 'saving'
                                                    ? { background: '#f0f4f8', color: '#9ea8b3', border: '1px solid #e4e9f0', cursor: 'default' }
                                                    : { background: '#f0f4f8', color: '#5a6474', border: '1px solid #e4e9f0', cursor: 'pointer', '&:hover': { background: alpha(PRIMARY, 0.06), color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } })
                                }}
                            >
                                {formSlugUnavailable                                        && <ErrorOutlined       sx={{ fontSize: 14 }} />}
                                {!formSlugUnavailable && saveStatus === 'saved'             && <CheckCircleOutlined sx={{ fontSize: 14 }} />}
                                {!formSlugUnavailable && saveStatus === 'error'             && <ErrorOutlined       sx={{ fontSize: 14 }} />}
                                {!formSlugUnavailable && !['saved','error'].includes(saveStatus) && <SaveOutlined  sx={{ fontSize: 14 }} />}
                                <Box component="span" sx={{ ml: 0.5 }}>
                                    {formSlugUnavailable ? 'Slug conflict' : saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Error' : 'Save'}
                                </Box>
                            </Box>
                        </Tooltip>
                    )}
                </Box>

                {/* Center: breakpoint selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', background: '#f0f4f8', border: '1px solid #e4e9f0', borderRadius: 2, p: '2px', gap: '2px' }}>
                        {BREAKPOINTS.map(bp => (
                            <Tooltip key={bp.key} title={bp.label}>
                                <IconButton size="small" onClick={() => setBreakpoint(bp.key)}
                                    sx={{ width: 32, height: 30, borderRadius: 1.5, color: breakpoint === bp.key ? PRIMARY : '#9ea8b3', background: breakpoint === bp.key ? '#fff' : 'transparent', boxShadow: breakpoint === bp.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                                    {bp.icon}
                                </IconButton>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>

                {/* Right: step/field count + gear + visit */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {activeFormId && (
                        <Tooltip title="Form settings">
                            <Box onClick={e => setSettingsAnchor(e.currentTarget)}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 2, cursor: 'pointer', color: settingsAnchor ? PRIMARY : '#5a6474', border: `1px solid ${settingsAnchor ? alpha(PRIMARY, 0.4) : '#e8edf2'}`, background: settingsAnchor ? alpha(PRIMARY, 0.06) : 'transparent', transition: 'all 0.15s', '&:hover': { color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } }}>
                                <SettingsOutlined sx={{ fontSize: 16 }} />
                            </Box>
                        </Tooltip>
                    )}

                    {activeFormId && visitUrl && (
                        <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e8edf2', borderRadius: 2, overflow: 'hidden' }}>
                            <Tooltip title="Save and open live form in a new tab">
                                <Box onClick={handleVisitForm}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.75, transition: 'all 0.15s', color: '#5a6474', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.04) } }}>
                                    <OpenInNewOutlined sx={{ fontSize: 16 }} />
                                    Visit Form
                                </Box>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Form Settings Popover */}
            <Popover
                open={Boolean(settingsAnchor)}
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: 1, borderRadius: 2, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e8edf2', width: 300, overflow: 'hidden' } } }}
            >
                <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsOutlined sx={{ fontSize: 14, color: PRIMARY }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1a1a2e' }}>Form Settings</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', ml: 'auto', fontStyle: 'italic', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeForm?.name}</Typography>
                </Box>
                <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
                    <FormSettingsPanel
                        settings={formSettings}
                        onChange={setFormSettings}
                        activeForm={activeForm}
                        event={event}
                        onSlugAvailabilityChange={avail => setFormSlugUnavailable(avail === false)}
                        loginMethodMismatch={loginMethodMismatch}
                        onResyncFields={handleResyncSystemFields}
                        onFormMetaChange={async (patch) => {
                            setForms(prev => prev.map(f => f._id === activeFormId ? { ...f, ...patch } : f));
                            try { await api.put(`/forms/${activeFormId}`, patch); } catch { /* best-effort */ }
                        }}
                    />
                </Box>
            </Popover>

            {/* Unpublished Form Modal */}
            <Dialog
                open={visitModalOpen}
                onClose={() => setVisitModalOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)', background: 'rgba(10,15,30,0.55)' } } }}
                PaperProps={{ sx: { borderRadius: 3, p: 0, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' } }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ background: alpha('#f59e0b', 0.08), borderBottom: '1px solid', borderColor: alpha('#f59e0b', 0.15), px: 3, pt: 3, pb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: alpha('#f59e0b', 0.12), border: `1.5px solid ${alpha('#f59e0b', 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PublicOutlined sx={{ fontSize: 24, color: '#d97706' }} />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Form not published</Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5 }}>
                                This form is currently a draft. Publish it so visitors can access and submit it.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ px: 3, py: 2.5, display: 'flex', gap: 1.5 }}>
                        <Box onClick={() => setVisitModalOpen(false)}
                            sx={{ flex: 1, py: 1.1, borderRadius: 2, border: '1px solid #e4e9f0', background: '#f8f9fb', color: '#5a6474', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', '&:hover': { background: '#f0f2f5', borderColor: '#d1d9e0' } }}>
                            Cancel
                        </Box>
                        <Box onClick={handlePublishAndVisitForm}
                            sx={{ flex: 1.4, py: 1.1, borderRadius: 2, background: PRIMARY, color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, '&:hover': { opacity: 0.88 } }}>
                            <PublicOutlined sx={{ fontSize: 15 }} />
                            Publish &amp; Visit Form
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ── Body ────────────────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* ── Left panel ───────────────────────────────────────────────── */}
                <Box onClick={e => e.stopPropagation()} sx={{ width: 230, flexShrink: 0, borderRight: '1px solid #e8edf2', background: '#fafbfc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <PanelTabs tabs={PANEL_TABS} active={leftTab} onChange={setLeftTab} accentColor={accentColor} />
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {!activeFormId ? (
                            <Box sx={{ p: 2, textAlign: 'center', pt: 4 }}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#b0bac8' }}>Select a form to edit</Typography>
                            </Box>
                        ) : isSuccessView ? (
                            <>
                                <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: '1px solid #f0f2f5', flexShrink: 0 }}>
                                    <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, background: alpha(SUCCESS_COLOR, 0.07), border: `1px solid ${alpha(SUCCESS_COLOR, 0.2)}` }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: SUCCESS_COLOR, fontWeight: 600, mb: 0.25 }}>Success screen</Typography>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#7a8494' }}>Edit copy and CTAs shown after submission.</Typography>
                                    </Box>
                                </Box>
                                <SuccessEditor successPage={formSettings.successPage} onChange={sp => setFormSettings(p => ({ ...p, successPage: sp }))} />
                            </>
                        ) : selectedId === HERO_BANNER ? (
                            <HeroBannerInspector settings={formSettings} onSettingsChange={setFormSettings} onClose={() => setSelectedId(null)} />
                        ) : (selectedId === HERO_TAGLINE || selectedId === HERO_TITLE || selectedId === HERO_SUBTITLE) ? (
                            <HeroTextInspector settings={formSettings} onSettingsChange={setFormSettings} onClose={() => setSelectedId(null)} />
                        ) : selectedField ? (
                            <FieldInspector field={selectedField} onChange={handleFieldChange} onClose={() => setSelectedId(null)} />
                        ) : leftTab === 'templates' ? (
                            <FormTemplatesPalette onApplyTemplate={handleApplyTemplate} />
                        ) : (
                            <FieldPalette fields={fields} />
                        )}
                    </Box>
                </Box>

                {/* ── Center: FormTabs + Canvas ────────────────────────────────── */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                    <Box onClick={e => e.stopPropagation()}>
                        <FormTabs
                            forms={forms}
                            activeFormId={activeFormId}
                            onSwitch={handleSwitchForm}
                            onAdd={handleAddForm}
                            onRename={handleRenameForm}
                            onDuplicate={handleDuplicateForm}
                            onDelete={handleDeleteForm}
                            loading={formsLoading}
                        />
                    </Box>

                    {/* No form selected — show wizard so the user can create one */}
                    {noFormSelected && (
                        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <FormWizard
                                onApplyTemplate={(template) => {
                                    // Stash the template; the load-active-form effect will pick it
                                    // up and apply it atomically, avoiding any race condition.
                                    window.__pendingTemplate__ = template;
                                    handleAddForm();
                                }}
                                onBlank={handleAddForm}
                            />
                        </Box>
                    )}

                    {/* Canvas shell */}
                    {activeFormId && (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {/* Form/Success tab bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, pt: 2, pb: 1, background: '#f0f4f8', flexShrink: 0 }}>
                                <Box sx={{ display: 'flex', background: '#e4e9f0', border: '1px solid #dce3eb', borderRadius: '10px', p: '3px', gap: '2px' }}>
                                    {[
                                        { key: 'form',    label: 'Form',    icon: <ViewQuiltOutlined  sx={{ fontSize: 14 }} />, color: FIELD_COLOR   },
                                        { key: 'success', label: 'Success', icon: <CheckCircleOutline sx={{ fontSize: 14 }} />, color: SUCCESS_COLOR },
                                    ].map(v => {
                                        const isAct = viewMode === v.key;
                                        return (
                                            <Box key={v.key}
                                                onClick={() => { setViewMode(v.key); setSelectedId(null); }}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.75, py: 0.6, borderRadius: '7px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: isAct ? 700 : 500, color: isAct ? v.color : '#9ea8b3', background: isAct ? '#fff' : 'transparent', boxShadow: isAct ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.12s', userSelect: 'none' }}>
                                                {v.icon}{v.label}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pt: 1.5, display: 'flex', justifyContent: 'center', background: '#f0f4f8' }}>
                            <Box sx={{
                                width: bpWidth, maxWidth: '100%',
                                transition: 'width 0.3s ease',
                                background: '#fff',
                                boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
                                minHeight: 400,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}>
                                {/* {fields.length === 0 && !isSuccessView ? (
                                    // Wizard shown inside an existing form that has no fields yet
                                    <FormWizard
                                        onApplyTemplate={handleApplyTemplate}
                                        onBlank={() => {
                                            const blank = makeField('text');
                                            pushHistory([blank]);
                                        }}
                                    />
                                ) : ( */}
                                    <FormCanvas
                                        layout={activeLayout}
                                        branding={activeBranding}
                                        settings={formSettings}
                                        isSuccessView={isSuccessView}
                                        currentStep={currentStep}
                                        activeStepId={activeStepId}
                                        fields={fields}
                                        selectedId={selectedId}
                                        onSelect={id => { setSelectedId(id); }}
                                        onDelete={handleDeleteField}
                                        onFieldChange={handleFieldChange}
                                        steps={steps}
                                        dragHandlers={dragHandlers}
                                        successPage={formSettings.successPage}
                                        onSuccessChange={sp => setFormSettings(p => ({ ...p, successPage: sp }))}
                                        onSettingsChange={setFormSettings}
                                        onNextStep={() => {
                                            const currentIdx = steps.findIndex(s => s._id === activeStepId);
                                            const next = steps[currentIdx + 1];
                                            if (next) handleSwitchStep(next._id);
                                        }}
                                        onShowSuccess={() => { setViewMode('success'); setSelectedId(null); }}
                                        stepHandlers={{
                                            onSwitch:  handleSwitchStep,
                                            onAdd:     handleAddStep,
                                            onRename:  handleRenameStep,
                                            onDelete:  handleDeleteStep,
                                            onReorder: handleReorderSteps,
                                        }}
                                    />
                                {/* )} */}
                            </Box>
                        </Box>
                        </Box>
                    )}

                </Box>
            </Box>
        </Box>
    );
}